<template>
  <div class="blacklist">
    <!-- Advertisers Level -->
    <section class="level-block">
      <div class="level-block-content">
        <h3 class="level-title">Advertisers Level</h3>
        <div class="toolbar">
          <a-button type="primary" @click="openAdvertiserModal">Add</a-button>
          <a-button type="primary" @click="downloadCsv('advertiser')">download template CSV</a-button>
          <a-upload :show-upload-list="false" :before-upload="(file) => onUploadCsv('advertiser', file)" accept=".csv">
            <a-button>Upload CSV</a-button>
          </a-upload>
         

        </div>
      </div>

      <a-spin :spinning="advertiserLoading">
        <a-table
          :columns="advertiserColumns"
          :data-source="advertiserList"
          :pagination="advertiserPagination"
          row-key="id"
          size="middle"
          @change="onAdvertiserTableChange"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'advertiser'">
              {{ record.advertiserName || record.name || '-' }}
            </template>
            <template v-else-if="column.key === 'addDate'">
              {{ formatDate(record.addDate || record.createTime) }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button
                type="link"
                class="action-link"
                @click="confirmRemoveAdvertiser(record)"
              >
                Remove
              </a-button>
            </template>
          </template>
        </a-table>
      </a-spin>
    </section>

    <!-- Affiliates Level -->
    <section class="level-block">
      <div class="level-block-content">
        <h3 class="level-title">Affiliates Level</h3>
        <div class="toolbar">
          <a-button type="primary" @click="openAffiliateModal">Add</a-button>
          <a-button type="primary" @click="downloadCsv('affiliate')">download template CSV</a-button>
          <a-upload :show-upload-list="false" :before-upload="(file) => onUploadCsv('affiliate', file)" accept=".csv">
            <a-button>Upload CSV</a-button>
          </a-upload>
       
        </div>
      </div>

      <a-spin :spinning="affiliateLoading">
        <a-table
          :columns="affiliateColumns"
          :data-source="affiliateList"
          :pagination="affiliatePagination"
          row-key="id"
          size="middle"
          @change="onAffiliateTableChange"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'advertiser'">
              {{ record.advertiserName || '-' }}
            </template>
            <template v-else-if="column.key === 'affiliate'">
              {{ record.affiliateName || record.name || '-' }}
            </template>
            <template v-else-if="column.key === 'addDate'">
              {{ formatDate(record.addDate || record.createTime) }}
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button
                type="link"
                class="action-link"
                @click="confirmRemoveAffiliate(record)"
              >
                Remove
              </a-button>
            </template>
          </template>
        </a-table>
      </a-spin>
    </section>

    <a-modal
      v-model:open="showAdvertiserForm"
      title="Add Advertiser"
      ok-text="Save"
      wrap-class-name="blacklist-add-modal"
      :confirm-loading="advertiserSaving"
      :get-container="getModalContainer"
      :z-index="1100"
      destroy-on-close
      :mask-closable="false"
      centered
      @ok="saveAdvertiser"
      @cancel="closeAdvertiserModal"
    >
      <div class="add-field">
        <div class="add-label">
          Select Advertiser
        </div>
        <a-select
          v-model:value="advertiserForm.advertiserId"
          show-search
          allow-clear
          placeholder="Please enter"
          style="width: 100%"
          :filter-option="false"
          :not-found-content="advertiserSearch.fetching ? undefined : null"
          :options="advertiserSearch.data"
          :get-popup-container="getPopupContainer"
          @search="onAdvertiserSearch"
        >
          <template
            v-if="advertiserSearch.fetching"
            #notFoundContent
          >
            <a-spin size="small" />
          </template>
        </a-select>
      </div>
    </a-modal>

    <a-modal
      v-model:open="showAffiliateForm"
      title="Add Affiliate"
      ok-text="Save"
      wrap-class-name="blacklist-add-modal"
      :confirm-loading="affiliateSaving"
      :get-container="getModalContainer"
      :z-index="1100"
      destroy-on-close
      :mask-closable="false"
      centered
      @ok="saveAffiliate"
      @cancel="closeAffiliateModal"
    >
      <div class="add-field">
        <div class="add-label">
          Select Advertiser
        </div>
        <a-select
          v-model:value="affiliateForm.advertiserId"
          show-search
          allow-clear
          placeholder="Please enter"
          style="width: 100%"
          :filter-option="false"
          :not-found-content="advertiserSearch.fetching ? undefined : null"
          :options="advertiserSearch.data"
          :get-popup-container="getPopupContainer"
          @search="onAdvertiserSearch"
          @change="onAffiliateAdvertiserChange"
        >
          <template
            v-if="advertiserSearch.fetching"
            #notFoundContent
          >
            <a-spin size="small" />
          </template>
        </a-select>
      </div>
      <div class="add-field">
        <div class="add-label">
          Select Affiliate
        </div>
        <a-select
          v-model:value="affiliateForm.affiliateId"
          show-search
          allow-clear
          :placeholder="affiliateForm.advertiserId ? 'Please enter' : 'Please select an advertiser first'"
          style="width: 100%"
          :options="affiliateOptions"
          :get-popup-container="getPopupContainer"
          @change="onAffiliateChange"
        />
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import moment from 'moment'
import { Modal, message } from 'ant-design-vue'
import { advertiser, influencers } from '../../server/index'

const props = defineProps({
  influencerId: { type: [String, Number], required: true },
})

const advertiserColumns = [
  { title: 'Advertiser', key: 'advertiser' },
  { title: 'AddDate', key: 'addDate', },
  { title: 'Action', key: 'action', },
]

const affiliateColumns = [
  { title: 'Advertiser', key: 'advertiser' },
  { title: 'Affiliate', key: 'affiliate' },
  { title: 'AddDate', key: 'addDate',  },
  { title: 'Action', key: 'action', },
]

const advertiserLoading = ref(false)
const affiliateLoading = ref(false)
const advertiserSaving = ref(false)
const affiliateSaving = ref(false)
const advertiserList = ref([])
const affiliateList = ref([])
const showAdvertiserForm = ref(false)
const showAffiliateForm = ref(false)
const affiliateOptions = ref([])

const advertiserPager = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
})
const affiliatePager = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
})

const advertiserPagination = computed(() => ({
  current: advertiserPager.current,
  pageSize: advertiserPager.pageSize,
  total: advertiserPager.total,
  showSizeChanger: true,
  showTotal: (total) => `Total ${total}`,
}))
const affiliatePagination = computed(() => ({
  current: affiliatePager.current,
  pageSize: affiliatePager.pageSize,
  total: affiliatePager.total,
  showSizeChanger: true,
  showTotal: (total) => `Total ${total}`,
}))

const advertiserForm = reactive({
  advertiserId: undefined,
})
const affiliateForm = reactive({
  advertiserId: undefined,
  affiliateId: undefined,
})
const advertiserSearch = reactive({
  data: [],
  fetching: false,
})

function formatDate (value) {
  if (!value) return '-'
  return moment(value).format('YYYY-MM-DD HH:mm:ss')
}

function getModalContainer () {
  return document.body
}

// 挂到触发节点父级，避免 modal transform 导致下拉偏移到视口外
function getPopupContainer (triggerNode) {
  return triggerNode?.parentNode || document.body
}

function onAdvertiserTableChange (pager) {
  if (pager?.current) {
    advertiserPager.current = pager.current
  }
  if (pager?.pageSize) {
    advertiserPager.pageSize = pager.pageSize
  }
  loadAdvertiserList()
}

function loadAdvertiserList () {
  if (!props.influencerId) return
  advertiserLoading.value = true
  influencers
    .getBlackListList(props.influencerId, {
      page: advertiserPager.current,
      size: advertiserPager.pageSize,
    })
    .then((res) => {
      if (res.success) {
        advertiserList.value = (res.data || []).map((item, index) => ({
          ...item,
          id: item.id ?? `adv-${item.advertiserId ?? index}`,
        }))
        advertiserPager.total = Number(res.total) || 0
      } else {
        message.error(res.error || res.message || 'Failed to load advertiser blacklist.')
      }
    })
    .finally(() => {
      advertiserLoading.value = false
    })
}

function onAffiliateTableChange (pager) {
  if (pager?.current) {
    affiliatePager.current = pager.current
  }
  if (pager?.pageSize) {
    affiliatePager.pageSize = pager.pageSize
  }
  loadAffiliateList()
}

function loadAffiliateList () {
  if (!props.influencerId) return
  affiliateLoading.value = true
  influencers
    .getAffiliateBlackListList(props.influencerId, {
      page: affiliatePager.current,
      size: affiliatePager.pageSize,
    })
    .then((res) => {
      if (res.success) {
        affiliateList.value = (res.data || []).map((item, index) => ({
          ...item,
          id: item.id ?? `aff-${item.affiliateId ?? index}`,
        }))
        affiliatePager.total = Number(res.total) || 0
      } else {
        message.error(res.error || res.message || 'Failed to load affiliate blacklist.')
      }
    })
    .finally(() => {
      affiliateLoading.value = false
    })
}

function openAdvertiserModal () {
  advertiserForm.advertiserId = undefined
  advertiserSearch.data = []
  showAdvertiserForm.value = true
}

function closeAdvertiserModal () {
  showAdvertiserForm.value = false
  advertiserForm.advertiserId = undefined
  advertiserSearch.data = []
}

function openAffiliateModal () {
  affiliateForm.advertiserId = undefined
  affiliateForm.affiliateId = undefined
  advertiserAffiliateRelationId.value = undefined
  affiliateOptions.value = []
  advertiserSearch.data = []
  showAffiliateForm.value = true
}

function closeAffiliateModal () {
  showAffiliateForm.value = false
  affiliateForm.advertiserId = undefined
  affiliateForm.affiliateId = undefined
  advertiserAffiliateRelationId.value = undefined
  affiliateOptions.value = []
  advertiserSearch.data = []
}

const onAdvertiserSearch = debounce((value) => {
  if (!value) {
    advertiserSearch.data = []
    return
  }
  advertiserSearch.fetching = true
  advertiser
    .getList({ advertiserName: value, size: 20 })
    .then((res) => {
      if (res.success) {
        advertiserSearch.data = (res.data || []).map((item) => ({
          label: item.advertiserName || item.name,
          value: item.id,
        }))
      }
    })
    .finally(() => {
      advertiserSearch.fetching = false
    })
}, 300)
const advertiserAffiliateRelationId = ref(undefined)

function onAffiliateAdvertiserChange (advertiserId) {
  affiliateForm.affiliateId = undefined
  advertiserAffiliateRelationId.value = undefined
  affiliateOptions.value = []
  if (!advertiserId) return
  advertiser.getAffiliateListById(advertiserId).then((res) => {
    if (res.success) {
      // item.id 即 advertiserAffiliateRelationId
      affiliateOptions.value = (res.data || []).map((item) => ({
        label: item.affiliate?.name || item.name || String(item.affiliateId || item.id),
        value: item.affiliateId ?? item.affiliate?.id ?? item.id,
        advertiserAffiliateRelationId: item.id,
      }))
    }
  })
}

// a-select @change: (value, option) —— option 上才有自定义字段
function onAffiliateChange (value, option) {
  if (!value) {
    advertiserAffiliateRelationId.value = undefined
    return
  }
  advertiserAffiliateRelationId.value =
    option?.advertiserAffiliateRelationId ??
    affiliateOptions.value.find((item) => item.value === value)
      ?.advertiserAffiliateRelationId
}

function saveAdvertiser () {
  if (!advertiserForm.advertiserId) {
    message.error('Please select an advertiser.')
    return Promise.reject()
  }
  advertiserSaving.value = true
  return influencers
    .addBlackList(props.influencerId, {
      advertiserId: advertiserForm.advertiserId,
    })
    .then((res) => {
      if (res.success) {
        message.success('Saved successfully.')
        closeAdvertiserModal()
        advertiserPager.current = 1
        loadAdvertiserList()
      } else {
        message.error(res.error || res.message || 'Save failed.')
        return Promise.reject()
      }
    })
    .finally(() => {
      advertiserSaving.value = false
    })
}

function saveAffiliate () {
  if (!affiliateForm.advertiserId) {
    message.error('Please select an advertiser.')
    return Promise.reject()
  }
  if (!affiliateForm.affiliateId) {
    message.error('Please select an affiliate.')
    return Promise.reject()
  }
  if (!advertiserAffiliateRelationId.value) {
    message.error('Missing advertiser-affiliate relation id.')
    return Promise.reject()
  }
  affiliateSaving.value = true
  return influencers
    .addAffiliateBlackList(props.influencerId, {
      advertiserAffiliateRelationId: advertiserAffiliateRelationId.value,
    })
    .then((res) => {
      if (res.success) {
        message.success('Saved successfully.')
        closeAffiliateModal()
        affiliatePager.current = 1
        loadAffiliateList()
      } else {
        message.error(res.error || res.message || 'Save failed.')
        return Promise.reject()
      }
    })
    .finally(() => {
      affiliateSaving.value = false
    })
}

function confirmRemoveAdvertiser (record) {
  const name = record.advertiserName || record.name || 'this advertiser'
  Modal.confirm({
    title: 'Remove Advertiser',
    content: `Are you sure you want to remove ${name}?`,
    okText: 'Confirm',
    cancelText: 'Cancel',
    centered: true,
    onOk () {
      return removeAdvertiser(record)
    },
  })
}

function removeAdvertiser (record) {
  return influencers
    .deleteBlackList(props.influencerId, {
      id: record.id,
      advertiserId: record.advertiserId,
    })
    .then((res) => {
      if (res.success) {
        message.success('Removed.')
        if (advertiserList.value.length <= 1 && advertiserPager.current > 1) {
          advertiserPager.current -= 1
        }
        loadAdvertiserList()
      } else {
        message.error(res.error || res.message || 'Remove failed.')
        return Promise.reject()
      }
    })
}

function confirmRemoveAffiliate (record) {
  const name = record.affiliateName || record.name || 'this affiliate'
  Modal.confirm({
    title: 'Remove Affiliate',
    content: `Are you sure you want to remove ${name}?`,
    okText: 'Confirm',
    cancelText: 'Cancel',
    centered: true,
    onOk () {
      return removeAffiliate(record)
    },
  })
}

function removeAffiliate (record) {
  return influencers
    .deleteAffiliateBlackList(props.influencerId, {
      id: record.id,
      affiliateId: record.affiliateId,
    })
    .then((res) => {
      if (res.success) {
        message.success('Removed.')
        if (affiliateList.value.length <= 1 && affiliatePager.current > 1) {
          affiliatePager.current -= 1
        }
        loadAffiliateList()
      } else {
        message.error(res.error || res.message || 'Remove failed.')
        return Promise.reject()
      }
    })
}

function downloadBlobCsv (filename, content) {
  // BOM 方便 Excel 正确识别 UTF-8
  const blob = new Blob(['\uFEFF' + content], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function downloadCsv (type) {
  if (type === 'advertiser') {
    // 格式：首行 AdvertiserID，其后每行一个广告商 ID
    downloadBlobCsv('advertiser-blacklist-template.csv', 'AdvertiserID\n')
    return
  }
  // 联盟黑名单：AdvertiserID,AffiliateID
  downloadBlobCsv(
    'affiliate-blacklist-template.csv',
    'AdvertiserID,AffiliateID\n'
  )
}

function onUploadCsv (type, file) {
  const formData = new FormData()
  formData.append('file', file)
  const request =
    type === 'advertiser'
      ? influencers.importBlackList(props.influencerId, formData)
      : influencers.importAffiliateBlackList(props.influencerId, formData)
  request.then((res) => {
    if (res.success) {
      message.success('Uploaded successfully.')
      if (type === 'advertiser') {
        advertiserPager.current = 1
        loadAdvertiserList()
      } else {
        affiliatePager.current = 1
        loadAffiliateList()
      }
    } else {
      message.error(res.error || res.message || 'Upload failed.')
    }
  })
  return false
}

function loadAll () {
  advertiserPager.current = 1
  affiliatePager.current = 1
  loadAdvertiserList()
  loadAffiliateList()
}

watch(
  () => props.influencerId,
  (id) => {
    if (id) loadAll()
  }
)

onMounted(() => {
  if (props.influencerId) loadAll()
})
</script>

<style lang="less" scoped>
.blacklist {
  padding-top: 8px;
}

.level-block {
  margin-bottom: 32px;

}

.level-block-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.level-title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.action-link {
  padding: 0;
}

.add-field {
  position: relative;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.add-label {
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.65);
}
</style>

<style lang="less">
/* modal 挂到 body，需非 scoped 才能作用到下拉容器 */
.blacklist-add-modal {
  .ant-modal-content,
  .ant-modal-body {
    overflow: visible;
  }
}
</style>
